import { useState, useMemo } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, CheckCircle2, CalendarDays } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const fmtDay = (d) => {
  if (!d) return "Sin fecha";
  try { return new Date(d + "T00:00:00").toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "short" }); }
  catch { return d; }
};

export default function PublishDayPanel({ tid, teetimes, onPublished }) {
  const [busy, setBusy] = useState(null);

  const days = useMemo(() => {
    const map = {};
    teetimes.forEach((t) => {
      if (!t.playing_date) return;
      const d = (map[t.playing_date] = map[t.playing_date] || { date: t.playing_date, docs: 0, drafts: 0, players: 0 });
      d.docs += 1;
      if (t.status !== "published") d.drafts += 1;
      d.players += t.total_players || 0;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [teetimes]);

  const publishDay = async (date) => {
    if (!window.confirm(`¿Publicar todas las salidas del ${fmtDay(date)} y notificar a los jugadores?`)) return;
    setBusy(date);
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/publish-day`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ playing_date: date }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      toast.success(`${d.published} tanda(s) publicada(s) · ${d.notified} jugador(es) notificado(s)`);
      onPublished?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  if (days.length === 0) return null;

  return (
    <div className="mb-4 border rounded-xl divide-y" data-testid="publish-day-panel">
      {days.map((d) => {
        const allPublished = d.drafts === 0;
        return (
          <div key={d.date} className="flex items-center gap-3 flex-wrap px-4 py-2.5" data-testid={`publish-day-${d.date}`}>
            <CalendarDays className="w-4 h-4 text-[#1B4D3E]" />
            <div className="flex-1 min-w-[160px]">
              <div className="text-sm font-semibold text-[#0F172A] capitalize">{fmtDay(d.date)}</div>
              <div className="text-xs text-[#64748B]">{d.docs} tanda(s) · {d.players} jugadores{d.drafts > 0 ? ` · ${d.drafts} en borrador` : ""}</div>
            </div>
            {allPublished ? (
              <span className="text-xs text-green-700 font-medium flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Publicado</span>
            ) : (
              <Button size="sm" disabled={busy === d.date} onClick={() => publishDay(d.date)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid={`publish-day-btn-${d.date}`}>
                <Send className="w-3.5 h-3.5 mr-1" /> {busy === d.date ? "Publicando..." : "Publicar día y notificar"}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
